/**
 * A single button that consolidates "Configure & Submit Requests" with
 * the agent submission-attempt states. Renders in place of TaskActionButton
 * for submit-request tasks that have a treatment_record_request.
 *
 * States:
 *  - not_found / no data: dark button → "Configure & Submit Requests" → opens manual flow
 *  - running:             indigo shimmer bar → disabled
 *  - ready:               dark button + green dot → "Review Submission Attempt" → opens modal
 *  - blocked:             dark button + amber dot → "Unblock Submission · N items" → opens modal
 *  - failed:              dark button + red dot → "View Submission Attempt" → opens modal
 */
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useGetRecordsRequestStatusQuery,
  useGetSubmissionAttemptDetailQuery,
} from '@/services/api/agentflowService';

import {
  getRecordsRequestAgentDisplayState,
  getRecordsRequestButtonLabel,
  getRecordsRequestStepLabel,
  getSubmissionAttemptDisplayState,
  isNewRecordsRequestBackendShape,
  RECORDS_REQUEST_AGENT_TERMINAL_STATUSES,
  type RecordsRequestAgentDisplayState as DisplayState,
} from './recordsRequestAgentState';
import { RecordsRequestEvaluationModal } from './RecordsRequestEvaluationModal';
import { SubmissionAttemptModal } from './submission-attempt';

// ---------------------------------------------------------------------------
// Shimmer animation for running state
// ---------------------------------------------------------------------------

const shimmerStyle = {
  background:
    'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0.4) 100%)',
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'shimmer-sweep 2.5s ease-in-out infinite',
} as const;

const DOT_COLORS: Record<DisplayState, string> = {
  running: 'bg-indigo-400 animate-pulse',
  ready: 'bg-green-400',
  blocked: 'bg-amber-400',
  failed: 'bg-red-400',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface RecordsRequestAgentButtonProps {
  matterId: string;
  treatmentRequestId: string;
  /** Opens the existing request modal / submission flow */
  onOpenRequestModal: () => void;
}

function isNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'status' in error &&
      error.status === 404,
  );
}

export const RecordsRequestAgentButton = ({
  matterId,
  treatmentRequestId,
  onOpenRequestModal,
}: RecordsRequestAgentButtonProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [hasTerminal, setHasTerminal] = useState(false);

  const {
    data,
    error: statusError,
    isLoading: isStatusLoading,
  } = useGetRecordsRequestStatusQuery(
    { matterId, requestId: treatmentRequestId },
    { pollingInterval: hasTerminal ? undefined : 5000 },
  );
  const {
    data: submissionAttempt,
    error: submissionAttemptError,
    isLoading: isSubmissionAttemptLoading,
  } = useGetSubmissionAttemptDetailQuery(
    { matterId, requestId: treatmentRequestId },
    {
      skip: !matterId || !treatmentRequestId,
      pollingInterval: modalOpen ? 3000 : undefined,
    },
  );

  useEffect(() => {
    if (data) {
      setHasTerminal(RECORDS_REQUEST_AGENT_TERMINAL_STATUSES.has(data.status));
    }
  }, [data]);

  const displayState =
    data && data.status !== 'not_found'
      ? getRecordsRequestAgentDisplayState(data)
      : getSubmissionAttemptDisplayState(submissionAttempt?.status);
  const blockerCount =
    data?.blocker_count ??
    data?.placeholder_count ??
    submissionAttempt?.blocker_count ??
    0;
  let useNewModal = false;
  if (submissionAttempt) {
    useNewModal = true;
  } else if (data) {
    useNewModal = isNewRecordsRequestBackendShape(data);
  }
  const evaluationRunId = data?.evaluation_run_id;
  const stepLabel = getRecordsRequestStepLabel(data);
  const hasUnknownAttemptState =
    !displayState &&
    (isStatusLoading ||
      isSubmissionAttemptLoading ||
      Boolean(statusError && !isNotFoundError(statusError)) ||
      Boolean(
        submissionAttemptError && !isNotFoundError(submissionAttemptError),
      ));
  const shouldShowSubmissionAttemptModal = useNewModal || hasUnknownAttemptState;
  const submissionAttemptModal = shouldShowSubmissionAttemptModal ? (
    <SubmissionAttemptModal
      open={modalOpen}
      onOpenChange={setModalOpen}
      onOpenRequestModal={onOpenRequestModal}
      matterId={matterId}
      treatmentRequestId={treatmentRequestId}
    />
  ) : null;

  // -------------------------------------------------------------------------
  // Unknown state → avoid collapsing errors/loading into "not started"
  // -------------------------------------------------------------------------
  if (hasUnknownAttemptState) {
    return (
      <>
        <Button
          type="button"
          onClick={() => setModalOpen(true)}
          size="sm"
          className="h-8 w-full justify-center rounded-xl bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800 gap-2"
        >
          {isStatusLoading || isSubmissionAttemptLoading ? (
            <>
              <span
                className={cn(
                  'inline-block h-2 w-2 shrink-0 rounded-full',
                  DOT_COLORS.running,
                )}
              />
              Checking Submission Attempt...
            </>
          ) : (
            <>
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-300" />
              View Submission Attempt
            </>
          )}
        </Button>

        {submissionAttemptModal}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Not started / confirmed absent → original dark "Configure & Submit" button
  // -------------------------------------------------------------------------
  if (!displayState) {
    return (
      <Button
        type="button"
        onClick={onOpenRequestModal}
        size="sm"
        className="h-8 w-full justify-center rounded-xl bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800"
      >
        Configure &amp; Submit Requests
      </Button>
    );
  }

  // -------------------------------------------------------------------------
  // Running → indigo shimmer bar
  // -------------------------------------------------------------------------
  if (displayState === 'running') {
    return (
      <>
        <style>{`@keyframes shimmer-sweep { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        <Button
          type="button"
          disabled
          size="sm"
          className="h-8 w-full justify-center rounded-xl bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-900 cursor-default gap-2"
        >
          <span
            className={cn(
              'inline-block h-2 w-2 shrink-0 rounded-full',
              DOT_COLORS.running,
            )}
          />
          <span className="truncate" style={shimmerStyle}>
            {stepLabel ?? 'Preparing submission...'}
          </span>
        </Button>
        {submissionAttemptModal}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Terminal states → dark button with dot + label, opens modal
  // -------------------------------------------------------------------------
  const label =
    submissionAttempt?.status === 'submitting'
      ? 'Submission In Flight'
      : getRecordsRequestButtonLabel(displayState, blockerCount);

  return (
    <>
      <Button
        type="button"
        onClick={() => setModalOpen(true)}
        size="sm"
        className="h-8 w-full justify-center rounded-xl bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800 gap-2"
      >
        <span
          className={cn(
            'inline-block h-2 w-2 shrink-0 rounded-full',
            DOT_COLORS[displayState],
          )}
        />
        {label}
      </Button>

      {/* New submission attempt modal */}
      {submissionAttemptModal}

      {/* Legacy evaluation modal */}
      {!useNewModal && evaluationRunId && (
        <RecordsRequestEvaluationModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          matterId={matterId}
          treatmentRequestId={treatmentRequestId}
          evaluationRunId={evaluationRunId}
        />
      )}
    </>
  );
};
